import { WebAPIs } from '../globals.ts';
import { getLogger } from '../logger.ts';
import { localNode } from '../local-node/core.ts';
import { type Authenticators, globalDomain } from '../domains/global/core.ts';
import { type Authenticator } from '../auth/core.ts';
import * as basic from '../auth/basic.ts';
import * as oauth2 from '../auth/oauth2.ts';
import * as custom from '../auth/custom.ts';
import { channelCluster } from '../mesh/channel-mesh.ts';
import type { WebSocketHeaderOptions } from "../mesh/ws-headers.ts";
import type { GatewayNode, MessageType, RequestType } from '../node.ts';
import type { LocalAddress } from '../state/types/common.ts';
import { agmDomain } from '../domains/agm/core.ts';
import { activityDomain } from '../domains/activity/core.ts';
import { metricsDomain, type MetricPublishers } from '../domains/metrics/core.ts';
import { metrics } from './metrics.ts';
import { contextDomain, ContextDomainOptions } from '../domains/context/core.ts';
import { busDomain } from '../domains/bus/core.ts';
import Scavenger from './scavenger.ts';
import { GATEWAY_VERSION } from '../versions.ts';
import { sanitizerOf, WithRequired } from '../common/utilities.ts';
import { meshNode } from '../mesh-node/core.ts';
import type { Domain } from '../domain.ts';
import { websocketBrokerMesh } from '../mesh/ws/broker/client.ts';
import { websocketCluster } from '../mesh/ws-mesh.ts';
import { restDirectory } from '../mesh/rest-directory.ts';
import { type ClientInfo, type Clients, addClient, removeClient } from './clients.ts';
import * as GatewayEncoders from '../common/encoders.ts';
import { IOGateway } from '../../gateway';
import { defaultPeerRestrictions, defaultRestrictions } from './visibility.ts';
import type { Mesh } from '../mesh.ts';
import { staticDirectory } from '../mesh/static-directory.ts';

const log = getLogger('gateway');

function authenticators(config?: IOGateway.AuthenticationConfig,
                        globals?: IOGateway.GatewayConfig['globals']): Authenticators {
    const available = (config?.available as Exclude<string, 'default' | 'available'>[] ?? ['basic']).reduce((a, k) => {
        if (k === 'basic') {
            a[k] = basic.authenticator(config?.basic ?? {});
            return a;
        }
        if (k === 'oauth2') {
            a[k] = oauth2.authenticator(config?.oauth2 ?? {}, { ...globals });
            return a;
        }
        const authConfig = config?.[k] ?? {};
        if (authConfig['authenticator']) {
            const authFn = authConfig['authenticator'];
            const configWithoutAuthenticator = {...authConfig};
            delete configWithoutAuthenticator['authenticator'];
            a[k] = custom.authenticator(configWithoutAuthenticator, authFn);
            return a;
        }
        return a;

    }, {} as Record<string, Authenticator>);
    return {'default': config?.default as string ?? 'basic', available};
}

function createWebSocketCluster(endpoint: string,
                                environment: { gateway: { version: string }, [key: string]: unknown },
                                relays?: string,
                                directory?: IOGateway.StaticMeshDirectoryConfig | IOGateway.RestMeshDirectoryConfig,
                                opts?: WebAPIs & WebSocketHeaderOptions): Mesh {
    let relaysUri = relays ?? endpoint;
    if (relays === undefined && relaysUri.startsWith('http')) {
        relaysUri = relaysUri.replace('http', 'ws');
        relaysUri += relaysUri.endsWith('/') ? '' : '/';
        relaysUri += 'relays';
    }
    if (directory?.['members'] !== undefined) {
        return websocketCluster(
            {
                relays: `${relaysUri}`
            },
            staticDirectory((directory as IOGateway.StaticMeshDirectoryConfig).members),
            opts);
    } else {
        const announceUri = (directory as IOGateway.RestMeshDirectoryConfig | undefined)?.uri ?? endpoint;
        const announceInterval = (directory as IOGateway.RestMeshDirectoryConfig | undefined)?.interval;
        const announceMetadata = { ...environment.gateway, ...(directory as IOGateway.RestMeshDirectoryConfig | undefined)?.metadata };
        return websocketCluster(
            {
                relays: `${relaysUri}`
            },
            restDirectory({uri: announceUri, announceInterval}, { metadata: announceMetadata, ...opts }),
            opts);
    }
}

function gatewayNode<T extends MessageType = MessageType>(domains: Domain<T>[],
                                                          environment: { gateway: { version: string }, [key: string]: unknown },
                                                          nodeId?: string,
                                                          signingKey?: string,
                                                          mesh?: IOGateway.MeshConfig,
                                                          globals?: IOGateway.GatewayConfig['globals']): GatewayNode {
    if (mesh) {
        nodeId = mesh.node ?? nodeId;

        const users = mesh.auth.user === null ? undefined : typeof mesh.auth.user === 'string' ? [mesh.auth.user] : mesh.auth.user;

        const clusterConfig = mesh.cluster;
        if (clusterConfig) {
            const { endpoint, relays, directory, opts } = clusterConfig;
            const cluster = createWebSocketCluster(endpoint, environment, relays, directory, { ...globals, ...opts });
            return meshNode(cluster, domains, { nodeId, signingKey, users });
        }

        const channel = mesh.channel;

        if (channel) {
            const cluster = channelCluster(channel);
            return meshNode(cluster, domains, { nodeId, signingKey, users });
        }

        const endpoint = mesh.broker?.endpoint;

        if (endpoint) {
            const cluster = websocketBrokerMesh(endpoint, globals);
            return meshNode(cluster, domains, {nodeId, signingKey, users});
        }

    }
    return localNode(domains, { nodeId, signingKey });
}

type GatewayState = {
    config: IOGateway.GatewayConfig,
    auth: Authenticators,
    factories: MetricPublishers,
    node: GatewayNode
    clients: Clients,
    scavenger: Scavenger,
    environment?: {endpoint?: string},
};

const codec = GatewayEncoders.direct<IOGateway.Message>({cljs: true});

const DEFAULT_CLIENT_BUFFER_SIZE = 100;
const INFO_DESCRIPTION = 'io.Gateway';
const INFO = {version: GATEWAY_VERSION, description: INFO_DESCRIPTION};

const CONFIG_LOG_SANITIZER = sanitizerOf(
    'token.key',
    "metrics.rest.authentication.password",
    "mesh.cluster.auth.basic.password",
    "mesh.cluster.auth.token.value"
);

class Gateway implements IOGateway.Gateway {

    #gateway: GatewayState | undefined;
    readonly #config: Readonly<IOGateway.GatewayConfig>;

    constructor(config: Readonly<IOGateway.GatewayConfig>) {
        this.#config = config;
    }

    protected async doStart(configuration: Readonly<IOGateway.GatewayConfig>,
                            environment?: {endpoint?: string }): Promise<GatewayState> {
        const auth = authenticators(configuration.authentication, configuration.globals);
        const configuredContextLifetime = configuration.contexts?.lifetime;
        const contextPeerRestrictions = defaultPeerRestrictions('context', configuration.peers?.visibility) ?? 'cluster';
        const contextOptions: ContextDomainOptions = {
            retainedOverride: configuredContextLifetime === 'retained' ? undefined : (configuredContextLifetime ?? 'ref-counted'),
            defaultPeerRestrictions: contextPeerRestrictions,
            defaultContextRestrictions: defaultRestrictions('context', configuration.contexts?.visibility)
        };
        const factories: MetricPublishers = await metrics(configuration.metrics, environment?.endpoint);
        const agmPeerRestrictions = (defaultPeerRestrictions('interop', configuration.peers?.visibility) ?? defaultPeerRestrictions('agm', configuration.peers?.visibility)) ?? 'local';
        const domains = [
            globalDomain(auth, {...contextOptions, welcomeInfo: INFO}),
            contextDomain(contextOptions),
            agmDomain({defaultPeerRestrictions: agmPeerRestrictions, defaultMethodRestrictions: defaultRestrictions('method', configuration.methods?.visibility)}),
            activityDomain(),
            busDomain({defaultPeerRestrictions: defaultPeerRestrictions('bus', configuration.peers?.visibility)}),
            metricsDomain(factories, configuration.metrics?.filters)];
        const node = gatewayNode(domains, { gateway: INFO, ...environment }, configuration.node, configuration.token?.key, configuration.mesh, configuration.globals);
        const clients: Clients = new Map<string, ClientInfo>();
        const scavenger = new Scavenger(clients, node, configuration.clients?.inactive_seconds ?? 0);
        return {config: configuration, auth, factories, node, clients, scavenger, environment};
    }

    protected async startGateway(configuration: IOGateway.GatewayConfig, environment?: {endpoint?: string}): Promise<GatewayState> {
        return await this.doStart(configuration, environment);
    }

    async start(environment?: { endpoint?: string }) {
        const cfg = this.#config;
        log.info(`starting gateway with environment:\n${JSON.stringify(environment)}\nconfiguration:${JSON.stringify(cfg, CONFIG_LOG_SANITIZER, '\t')}`);
        this.#gateway = await this.startGateway(cfg, environment);
        return this;
    }

    protected async doStop(gateway: GatewayState) {
        log.info(`stopping gateway [${JSON.stringify({ environment: gateway.environment, clients: [...gateway.clients.keys()]})}]`);
        Object.values(gateway.clients).forEach(c => {
            if (c.onStop) {
                c.onStop();
            }
        });
        gateway.scavenger.stop();
        gateway.node.close();
        for (const f of gateway.factories) {
            await f.shutdown();
        }
        Object.values(gateway.auth.available).forEach((a) => a.stop());
    }

    async stop() {
        if (this.#gateway) {
            await this.doStop(this.#gateway);
            this.#gateway = undefined;
        }
        return this;
    }

    info(): { version: string, description: string, endpoint?: string } {
        return {...INFO, endpoint: this.#gateway?.environment?.endpoint};
    }

    private cnt = 0;

    async connect(handler: (client: IOGateway.GatewayClient, msg: IOGateway.Message) => void): Promise<IOGateway.GatewayClient> {
        if (!this.#gateway) {
            throw new Error(`(no gateway) did you call start?`);
        }
        const gw = this.#gateway;

        const bufferSize = this.#gateway?.config.clients?.buffer_size ?? DEFAULT_CLIENT_BUFFER_SIZE;
        log.info(`local client connected, buffer-size: ${bufferSize}`);

        const key = `local:${++this.cnt}`;
        return new GatewayClient(handler, gw, {key, codec});

    }

    client<T>(handler: ((this: IOGateway.GatewayClient<T>, msg: T) => void),
              opts?: IOGateway.GatewayClientOptions<T>): IOGateway.GatewayClient<T> {
        if (!this.#gateway) {
            throw new Error(`(no gateway) did you call start() and waited for completion?`);
        }
        if (handler.length !== 1) {
            throw new Error(`expecting function with one arg`);
        }
        const key = `local:${++this.cnt}`;
        const gw = this.#gateway;

        return new GatewayClient(handler, gw, {key, codec: codec as unknown as  GatewayEncoders.Codec<IOGateway.Message, T>, ...opts});

    }
}

class GatewayClient<T> implements IOGateway.GatewayClient<T> {
    private readonly receive: (this: GatewayClient<T>, msg: T) => void;
    readonly key: string;
    private readonly codec: GatewayEncoders.Codec<IOGateway.Message, T>;
    private readonly host?: string;
    private readonly ping?: (this:GatewayClient<T>, payload?: unknown) => void;
    private readonly auth?: (this: GatewayClient<T>, request: IOGateway.Auth.AuthenticationRequest) => Promise<IOGateway.Auth.AuthenticationSuccess>
    constructor(receive: ((this: IOGateway.GatewayClient<T>, msg: T) => void) | ((client: IOGateway.GatewayClient, msg: IOGateway.Message) => void),
                private readonly gw: GatewayState,
                opts: WithRequired<IOGateway.GatewayClientOptions<T>, 'key' | 'codec'> & {host?: string}) {

        function handler(this: IOGateway.GatewayClient<T>, msg: T): void {
            (receive as ((client: IOGateway.GatewayClient, msg: IOGateway.Message) => void))(this as IOGateway.GatewayClient, msg as IOGateway.Message);
        }
        this.receive = receive.length === 1 ? receive as ((this: GatewayClient<T>, msg: T) => void): handler.bind(this);
        this.key = opts.key;
        this.codec = opts.codec;
        this.host = opts.host;
        this.ping = opts.onPing?.bind(this);
        this.auth = opts.onAuthenticate?.bind(this);
        const source: LocalAddress = {
            type: 'local',
            receive: (msg) => {
                let m;
                try {
                    if (msg.type === 'ping' && this.ping) {
                        if (log.enabledFor('info')) {
                            log.info(`checking ${this.key}`);
                        }
                        this.ping();
                    }
                    else {
                        m = this.codec.encode(msg);
                        if (log.enabledFor('trace')) {
                            log.debug(`sending message ${m} to ${this.key}`);
                        }
                        Promise.resolve().then(() => {
                            try {
                                this.receive(m)
                            } catch (e) {
                                log.error(`custom client error, that we're looking for ${m}`, e);
                            }
                        });
                    }
                } catch (e) {
                    log.error(`unable to send message ${m}`, e);
                }
            },
            endpoint: this.key,
            host: this.host,
            auth: this.auth
        }
        const onDisconnect = opts.onDisconnect?.bind(this);
        addClient(this.gw.clients, this.gw.node, this.key, source, onDisconnect ? () => {
            onDisconnect('inactive');
        }: undefined, onDisconnect ? () => {
            onDisconnect('shutdown');
        } : undefined);

    }

    close(): void {
        removeClient(this.gw.clients, this.gw.node, this.key);
    }
    // backward compatibility
    async disconnect() {
        this.close();
        return true;
    }

    send(msg: T): void {
        try {
            const message = this.codec.decode(msg) as IOGateway.Message<RequestType>;
            if (log.enabledFor('debug')) {
                log.debug(`processing incoming message from client [${this.key}]`);
            }
            const client = this.gw.clients.get(this.key);
            if (client?.source) {
                if (message.type === 'ping') {
                    const lastAccess = Date.now();
                    if (log.enabledFor('debug')) {
                        log.debug(`received ping from ${this.key} at ${lastAccess}`);
                    }
                    client.lastAccess = lastAccess;
                } else {
                    this.gw.node.message({origin: 'local', source: client.source, body: message});
                }
            } else {
                log.warn(`cannot process message from non-registered client key ${this.key}`);
            }
        } catch (ex) {
            this.receive(ex?.['message']);
        }
    }
}
export const Factory = (config: IOGateway.GatewayConfig): IOGateway.Gateway => {
    return new Gateway(config);
}
